import { useEffect, useState } from 'react';
import { AlarmClock, Archive, ArchiveRestore, BellOff, BellRing, ClipboardCopy, FileQuestion, Link2, MoreHorizontal, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import type { TaskBoard, TaskContextRef, TaskDetail, TaskUpdateInput } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { StatusSelect } from '@/components/StatusSelect';
import type { StatusMeta } from '@/components/status-tone';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AppAlert } from '@/components/AppAlert';
import { EmptyState } from '@/components/EmptyState';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { SnoozeMenu } from '@/components/SnoozeMenu';
import { isSnoozed, snoozeDay } from '@/lib/snooze';
import { markTaskNotificationsRead, moveTask, setTaskFollowing, summariseTask, updateTask } from '@/lib/tasks-api';
import { describeApiError } from '@/lib/api-errors';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { TaskAiSummary } from './TaskAiSummary';
import { TaskAttachments } from './TaskAttachments';
import { TaskAttachmentViewerProvider } from './TaskAttachmentViewer';
import { TaskDecisions } from './TaskDecisions';
import { TaskDescription } from './TaskDescription';
import { TaskProperties } from './TaskProperties';
import { TaskTimeline } from './TaskTimeline';
import { TaskChatComposer } from './TaskChatComposer';
import { AiBuilderMentions } from './ai-builder-mention';
import { TaskRepeatDialog } from './TaskRepeatDialog';
import { repeatNextLabel } from './task-repeat-label';
import { describeTaskRepeat, type TaskRepeat } from '@shared/task-repeat';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTaskDetail } from './useTasks';
import { optimisticUpdate } from '@/stores/taskOptimistic';
import { isStaleWriteCode } from '@shared/types/stale-write';
import { useTaskStore } from '@/stores/taskStore';
import { compareRank, rankBetween } from '@shared/task-rank';
import { attachFilesToTask } from './task-files';
import { useFileDrop, usePreventFileNavigation } from '@/components/useFileDrop';
import { copyTasksMarkdown } from './copy-task-markdown';
import { copyWithToast } from '@/lib/clipboard';
import { formatTaskNumber } from '@shared/task-number';
import { PaneResizeHandle } from '@/components/PaneResizeHandle';
import { usePref } from '@/hooks/use-pref';
import { UI_PREFS, withCodec } from '@/lib/ui-prefs';

/** The Board pop-up's activity pane (T-350): 24rem until it's dragged, at least 20rem, leaving the task 28rem. */
const ACTIVITY_MIN_WIDTH = 320;
const TASK_MIN_WIDTH = 448;
const ACTIVITY_WIDTH = withCodec(UI_PREFS.tasksActivityWidth, {
  defaultValue: 384,
  parse: (raw) => {
    const stored = Number(raw);
    return Number.isFinite(stored) && stored >= ACTIVITY_MIN_WIDTH ? Math.round(stored) : undefined;
  },
  serialize: (width) => String(width),
});

type BoardMeta = Pick<TaskBoard, 'members' | 'labels' | 'columns' | 'types' | 'ai_builder'>;

/**
 * The Board's columns as statuses, for the column picker. Teams name their own columns, so the tone
 * comes from where a column sits: the first is not started, a done column is done, the rest are
 * under way.
 */
function taskColumnStatuses(columns: BoardMeta['columns']): Record<string, StatusMeta> {
  return Object.fromEntries(
    columns.map((c, i) => [
      c.id,
      { label: c.name, tone: c.is_done ? 'success' : i === 0 ? 'neutral' : 'info' } satisfies StatusMeta,
    ])
  );
}

/** Everything about one task. `layout="wide"` for the Board modal, `"narrow"` for the Tasks panel. */
export function TaskDetailView({
  taskId,
  meta,
  layout = 'wide',
  currentPage,
  onNavigate,
}: {
  taskId: string;
  meta: BoardMeta;
  layout?: 'wide' | 'narrow';
  /** The page's own Context, offered first when linking. */
  currentPage?: (TaskContextRef & { title: string }) | null;
  /** Called when a link inside the task navigates away (so a modal can close). */
  onNavigate?: () => void;
}) {
  const { task, hydrated, error, notFound, reload } = useTaskDetail(taskId);
  const isMobile = useIsMobile();
  // The activity pane's width: saved per browser when a drag ends, followed live while dragging.
  const [savedActivityWidth, saveActivityWidth] = usePref(ACTIVITY_WIDTH);
  const [draggedActivityWidth, setDraggedActivityWidth] = useState<number | null>(null);
  const activityWidth = draggedActivityWidth ?? savedActivityWidth;
  const boards = useTaskStore((s) => s.boards);
  const meId = useAuthStore((s) => s.user?.id);
  // A title draft exists only while typing; otherwise the task's own title shows (live updates
  // included). It remembers its task so switching tasks never carries it over.
  const [titleDraft, setTitleDraft] = useState<{ taskId: string; text: string } | null>(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [summarising, setSummarising] = useState(false);
  // Files dropped anywhere on the task are posted to it (the chat box keeps its own drops).
  const drop = useFileDrop((files) => void attachFilesToTask(taskId, files));
  usePreventFileNavigation();

  useEffect(() => {
    void markTaskNotificationsRead({ task_id: taskId });
  }, [taskId]);

  if (!task) {
    // The same words as a record page (RecordPageState); the pop-up or panel is the way back.
    if (notFound) {
      return (
        <div className="p-4">
          <EmptyState
            icon={<FileQuestion className="size-5" />}
            title="This task doesn't exist"
            description="It may have been deleted, or the link is wrong."
          />
        </div>
      );
    }
    return error ? (
      <div className="p-4">
        <LoadError title="Couldn't load this task" error={error} onRetry={() => void reload()} />
      </div>
    ) : (
      <LoadingState label="Loading task…" />
    );
  }

  // Every change shows at once; the request confirms it or undoes it (see taskOptimistic).
  const run = (request: Promise<{ error?: string }>, fields?: Partial<TaskDetail>) =>
    optimisticUpdate(task.id, { fields }, request);

  const patch = (input: TaskUpdateInput, fields?: Partial<TaskDetail>) => run(updateTask(task.id, input), fields);

  /** A description save answers with its response: a stale one is shown by TaskDescription's banner, not a toast. */
  const saveDescription = async (input: TaskUpdateInput, fields?: Partial<TaskDetail>) => {
    const request = updateTask(task.id, input);
    await optimisticUpdate(task.id, { fields }, request, { quiet: (res) => isStaleWriteCode(res.code) });
    return request;
  };

  /** Moving from here puts the card at the bottom of its new column, as the server will. */
  const moveTo = (columnId: string) => {
    const ranks = Object.values(boards).flatMap((b) =>
      (b.data?.tasks ?? []).filter((t) => t.column_id === columnId && t.id !== task.id).map((t) => t.rank)
    );
    const last = ranks.sort(compareRank).at(-1) ?? null;
    const done = meta.columns.find((c) => c.id === columnId)?.is_done;
    void run(moveTask(task.id, { column_id: columnId }), {
      column_id: columnId,
      rank: rankBetween(last, null),
      done_at: done ? task.done_at ?? new Date().toISOString() : null,
      // Another column ends a snooze, as the server does.
      ...(columnId !== task.column_id ? { snoozed_until: null } : {}),
    });
  };

  // Snoozed (T-50): off the Board until then; it comes back in the same column.
  const snoozed = isSnoozed(task.snoozed_until);
  const snooze = (until: Date | null) => {
    const at = until ? until.toISOString() : null;
    void patch({ snoozed_until: at }, { snoozed_until: at }).then((ok) => {
      if (ok) toast.success(until ? `Snoozed until ${snoozeDay(until)}` : 'Back on the Board');
    });
  };

  // Repeat (T-49): it comes back on a schedule, done or not.
  const repeatText = task.repeat ? describeTaskRepeat(task.repeat) : null;
  const saveRepeat = (repeat: TaskRepeat | null, nextAt: Date | null) => {
    setRepeatOpen(false);
    const next_at = nextAt ? nextAt.toISOString() : null;
    void patch({ repeat }, { repeat, repeat_next_at: next_at }).then((ok) => {
      if (ok) toast.success(repeat ? `${describeTaskRepeat(repeat)}: next back ${repeatNextLabel(nextAt!)}` : 'It won’t repeat any more');
    });
  };

  const title = titleDraft?.taskId === task.id ? titleDraft.text : task.title;
  const saveTitle = () => {
    const next = title.trim();
    setTitleDraft(null);
    if (next && next !== task.title) void patch({ title: next }, { title: next });
  };

  const column = meta.columns.find((c) => c.id === task.column_id);
  const columnStatuses = taskColumnStatuses(meta.columns);
  // The Board pop-up on a desktop: two panes, like Trello. The task on the left, its activity
  // and chat on the right, each scrolling on its own. Phones and the Tasks panel: one column.
  const panes = layout === 'wide' && !isMobile;
  // Phones get the pop-up full screen, with its close button over the top right.
  const underCloseButton = layout === 'wide' && isMobile;

  const copyLink = () => void copyWithToast(`${window.location.origin}/tasks/${task.id}`, 'the link');

  /** The whole task for pasting into an AI chat. */
  const copyMarkdown = () => {
    copyTasksMarkdown([task.id], meta, [task]).then(
      () => toast.success('Markdown copied'),
      () => toast.error('Couldn’t copy the task')
    );
  };

  const toolbar = (
    <div className={cn('flex items-center gap-0.5', underCloseButton && 'pr-8')}>
      {column ? (
        <StatusSelect value={column.id} statuses={columnStatuses} onValueChange={moveTo} aria-label="Column" />
      ) : null}
      {task.number ? (
        <span className="ml-2 text-xs tabular-nums text-muted-foreground" data-hint="Task number: search for it, or use it in the API">
          {formatTaskNumber(task.number)}
        </span>
      ) : null}
      <span className="flex-1" />
      {/* Done and archived tasks are off the Board already. */}
      {!column?.is_done && !task.archived ? (
        <SnoozeMenu
          snoozedUntil={snoozed ? task.snoozed_until : null}
          open={snoozeOpen}
          onOpenChange={setSnoozeOpen}
          onSnooze={snooze}
          backLabel="Back on the Board"
          align="end"
          trigger={
            <Button
              variant="ghost"
              size="icon-sm"
              className={cn(snoozed && 'text-info-strong')}
              aria-label={snoozed ? `Snoozed until ${snoozeDay(task.snoozed_until!)}` : 'Snooze'}
              data-hint={snoozed ? `Snoozed until ${snoozeDay(task.snoozed_until!)}: click to change or end it` : 'Snooze: off the Board until a day you choose'}
            >
              <AlarmClock className="size-4" />
            </Button>
          }
        />
      ) : null}
      <Button
        variant="ghost"
        size="icon-sm"
        className={cn(task.repeat && 'text-info-strong')}
        aria-label={repeatText ? `Repeats: ${repeatText}` : 'Repeat'}
        data-hint={repeatText ? `${repeatText}: click to change or stop it` : 'Repeat: bring it back every week, month…'}
        onClick={() => setRepeatOpen(true)}
      >
        <Repeat className="size-4" />
      </Button>
      <TaskRepeatDialog open={repeatOpen} onOpenChange={setRepeatOpen} repeat={task.repeat} onSave={saveRepeat} />
      <Button
        variant="ghost"
        size="icon-sm"
        className={cn(task.following && 'text-primary')}
        aria-label={task.following ? 'Unfollow' : 'Follow'}
        data-hint={task.following ? 'Following: you get notified. Click to stop.' : 'Follow to get notified'}
        onClick={() => {
          const following = !task.following;
          // The card's followers too, so the Board's Following view changes at once (T-295).
          const others = task.follower_ids.filter((id) => id !== meId);
          const follower_ids = following && meId ? [...others, meId] : others;
          void run(setTaskFollowing(task.id, following), { following, follower_ids });
        }}
      >
        {task.following ? <BellRing className="size-4" /> : <BellOff className="size-4" />}
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label="Copy link" data-hint="Copy link" onClick={copyLink}>
        <Link2 className="size-4" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="More actions">
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={copyMarkdown}>
            <ClipboardCopy className="size-4" />
            Copy Markdown
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => void patch({ archived: !task.archived }, { archived: !task.archived, ...(task.archived ? {} : { snoozed_until: null }) })}
          >
            {task.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
            {task.archived ? 'Put back on the Board' : 'Archive'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const summarise = async () => {
    setSummarising(true);
    const res = await summariseTask(task.id);
    setSummarising(false);
    if (res.error) toast.error(describeApiError(res));
    else void reload();
  };

  // Above the activity (T-318): what the task is and where it stands, for someone catching up. In one
  // column (phones, the Tasks panel) it goes above the description instead, once there is one (T-331).
  const summary = (
    <TaskAiSummary
      summary={task.ai_summary}
      dueAt={task.ai_summary_due_at}
      onSummarise={() => void summarise()}
      summarising={summarising}
      className={panes ? 'mb-3 shrink-0' : undefined}
    />
  );

  const timeline = (
    <TaskTimeline
      task={task}
      hydrated={hydrated}
      showComposer={!isMobile}
      fill={panes}
      members={meta.members}
      labels={meta.labels}
      onNavigate={onNavigate}
    />
  );

  const view = (
    // Any file on the task, in the Attachments list or the chat, opens in one viewer.
    <TaskAttachmentViewerProvider task={task} members={meta.members} onNavigate={onNavigate}>
      {/* data-task-view: pasting a file anywhere in here attaches it to this task (see TaskChatComposer).
          min-w-0: in the pop-up's grid, a long chip must not widen the whole task past the screen. */}
      <div
        className={cn(
          'relative min-w-0',
          panes ? 'grid h-full min-h-0' : 'space-y-5'
        )}
        // A narrower window than when it was saved still leaves the task its minimum.
        style={panes ? { gridTemplateColumns: `minmax(0,1fr) max(${ACTIVITY_MIN_WIDTH}px, min(${activityWidth}px, 100% - ${TASK_MIN_WIDTH}px))` } : undefined}
        data-task-view={task.id}
        {...drop.bind}
      >
        {drop.dragging ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-background/80 text-sm font-medium text-primary">
            Drop to add to this task
          </div>
        ) : null}

        <div className={cn('min-w-0 space-y-5', panes && 'overflow-y-auto p-6')}>
          {/* The controls get their own row, so the title has the full width to wrap in. */}
          <header className="space-y-1">
            {toolbar}
            {/* A one-line textarea so long titles wrap; Enter still saves. */}
            <Textarea
              value={title}
              rows={1}
              onChange={(e) => setTitleDraft({ taskId: task.id, text: e.target.value.replace(/\n/g, ' ') })}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
                if (e.key === 'Escape') setTitleDraft(null);
              }}
              aria-label="Task title"
              className="-ml-2 min-h-0 resize-none border-transparent px-2 py-1 text-xl font-semibold leading-snug shadow-none hover:border-input focus-visible:border-input md:text-xl"
            />
          </header>

          {task.archived ? (
            <AppAlert tone="neutral" compact role="status" icon={<Archive />}>
              This task is archived, so it isn’t on the Board.
            </AppAlert>
          ) : snoozed ? (
            <AppAlert
              tone="info"
              compact
              role="status"
              icon={<AlarmClock />}
              action={
                <Button variant="outline" size="xs" onClick={() => snooze(null)}>
                  Unsnooze
                </Button>
              }
            >
              Snoozed until {snoozeDay(task.snoozed_until!)}, so it isn’t on the Board.
            </AppAlert>
          ) : null}
          {task.repeat ? (
            <AppAlert
              tone="info"
              compact
              role="status"
              icon={<Repeat />}
              action={
                <Button variant="outline" size="xs" onClick={() => setRepeatOpen(true)}>
                  Change
                </Button>
              }
            >
              <span>
                {repeatText}
                {task.repeat.new_task ? ', as a new task' : null}
                {task.repeat_next_at ? <>. Next back {repeatNextLabel(task.repeat_next_at)}</> : null}
              </span>
            </AppAlert>
          ) : null}

          <TaskProperties
            task={task}
            meta={meta}
            patch={patch}
            run={run}
            currentPage={currentPage}
            onNavigate={onNavigate}
            inline={panes}
          />
          {!panes && task.ai_summary ? summary : null}
          <TaskDescription
            key={task.id}
            task={task}
            hydrated={hydrated}
            members={meta.members}
            saveDescription={saveDescription}
            onReload={reload}
            onNavigate={onNavigate}
          />
          <TaskDecisions taskId={task.id} events={task.events} members={meta.members} />
          <TaskAttachments task={task} />
          {panes ? null : timeline}
        </div>

        {panes ? (
          // pt-12: clear of the pop-up's close button.
          <div className="relative flex min-h-0 flex-col border-l bg-muted/30 px-4 pb-4 pt-12">
            {/* Drag the border to widen the chat for a long conversation. */}
            <PaneResizeHandle
              width={activityWidth}
              min={ACTIVITY_MIN_WIDTH}
              max={(handle) => (handle.parentElement?.parentElement?.clientWidth ?? window.innerWidth) - TASK_MIN_WIDTH}
              grows="left"
              onResize={setDraggedActivityWidth}
              onResizeEnd={(width) => {
                saveActivityWidth(width);
                setDraggedActivityWidth(null);
              }}
              label="Resize the activity pane"
              className="absolute inset-y-0 -left-2 z-10"
            />
            {summary}
            {timeline}
          </div>
        ) : null}

        {isMobile ? (
          // Phones: the chat box stays pinned to the bottom of the screen while you scroll the task.
          // In the full-screen pop-up, -bottom-4 reaches past its padding to the very bottom.
          <div
            className={cn(
              'sticky bottom-0 z-10 border-t bg-background pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3',
              underCloseButton && '-bottom-4'
            )}
          >
            <TaskChatComposer taskId={task.id} members={meta.members} />
          </div>
        ) : null}
      </div>
    </TaskAttachmentViewerProvider>
  );
  // Its text can @ the AI builder when the team has it (T-393).
  return <AiBuilderMentions.Provider value={meta.ai_builder}>{view}</AiBuilderMentions.Provider>;
}
